/**
 * Web component for displaying Anear QR codes
 *
 * Usage:
 *   <anear-qr-code></anear-qr-code>
 *   <anear-qr-code expand></anear-qr-code>
 *
 * The component will be initialized by useAppDisplayDomSetup with:
 * - qrImageUrl: The URL of the QR code image
 * - eventName: The name of the event (for alt text)
 * - onExpand: Callback function to open the QR dialog (if expand is enabled)
 */
class AnearQrCode extends HTMLElement {
  constructor() {
    super()
    this._qrImageUrl = null
    this._eventName = null
    this._onExpand = null
    this._img = null
  }

  static get observedAttributes() {
    return ['expand']
  }

  connectedCallback() {
    // Set default display style if not already set
    if (!this.style.display) {
      this.style.display = 'block'
    }
    this.render()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'expand') {
      this.updateClickHandler()
    }
  }

  /**
   * Initialize the component with event data
   * Called by useAppDisplayDomSetup
   */
  initialize(qrImageUrl, eventName, onExpand) {
    this._qrImageUrl = qrImageUrl
    this._eventName = eventName
    this._onExpand = onExpand
    this.render()
  }

  get expand() {
    return this.hasAttribute('expand')
  }

  set expand(value) {
    if (value) {
      this.setAttribute('expand', '')
    } else {
      this.removeAttribute('expand')
    }
  }

  render() {
    if (!this._img) {
      // Create the image element
      this._img = document.createElement('img')
      this._img.style.display = 'block'
      this._img.style.width = '100%'
      this._img.style.height = '100%'
      this._img.style.objectFit = 'contain'
      this._img.style.imageRendering = 'pixelated'
      this._img.style.backgroundColor = 'white'
      this.appendChild(this._img)
    }

    // Update image properties
    if (this._qrImageUrl) {
      this._img.src = this._qrImageUrl
    }
    if (this._eventName) {
      this._img.alt = `QR Code for ${this._eventName}`
    }

    this.updateClickHandler()
  }

  updateClickHandler() {
    if (!this._img) return

    const shouldExpand = this.expand && this._onExpand
    this._img.style.cursor = shouldExpand ? 'pointer' : ''
    this._img.onclick = shouldExpand ? (e) => {
      e.stopPropagation()
      this._onExpand()
    } : null
  }
}

// Register the custom element
if (!customElements.get('anear-qr-code')) {
  customElements.define('anear-qr-code', AnearQrCode)
}

export default AnearQrCode
